import type { UndoRedoState, MemoryBookSnapshot } from './types'

// Constants
export const MAX_HISTORY_SIZE = 20

// Action types
export const PUSH_HISTORY = 'PUSH_HISTORY'
export const UNDO = 'UNDO'
export const REDO = 'REDO'
export const CLEAR_HISTORY = 'CLEAR_HISTORY'
export const CLEAR_REDO_HISTORY = 'CLEAR_REDO_HISTORY'
export const RESTORE_HISTORY = 'RESTORE_HISTORY'

// Action interfaces
interface PushHistoryAction {
  type: typeof PUSH_HISTORY
  payload: {
    snapshot: MemoryBookSnapshot
  }
}

interface UndoAction {
  type: typeof UNDO
  payload: {
    currentState: MemoryBookSnapshot
  }
}

interface RedoAction {
  type: typeof REDO
  payload: {
    currentState: MemoryBookSnapshot
  }
}

interface ClearHistoryAction {
  type: typeof CLEAR_HISTORY
}

interface ClearRedoHistoryAction {
  type: typeof CLEAR_REDO_HISTORY
}

interface RestoreHistoryAction {
  type: typeof RESTORE_HISTORY
  payload: {
    undoStack: MemoryBookSnapshot[]
    redoStack: MemoryBookSnapshot[]
  }
}

type UndoRedoAction =
  | PushHistoryAction
  | UndoAction
  | RedoAction
  | ClearHistoryAction
  | ClearRedoHistoryAction
  | RestoreHistoryAction

// Initial state
export const initialUndoRedoState: UndoRedoState = {
  undoStack: [],
  redoStack: [],
}

// Helper function to enforce max stack size
// Keeps the most recent N items if stack exceeds limit
function enforceMaxSize(stack: MemoryBookSnapshot[]): MemoryBookSnapshot[] {
  if (stack.length <= MAX_HISTORY_SIZE) return stack
  return stack.slice(-MAX_HISTORY_SIZE)
}

// Reducer
export function undoRedoReducer(state: UndoRedoState, action: UndoRedoAction): UndoRedoState {
  switch (action.type) {
    case PUSH_HISTORY: {
      const { snapshot } = action.payload
      const newUndoStack = enforceMaxSize([...state.undoStack, snapshot])

      return {
        undoStack: newUndoStack,
        redoStack: [], // Clear redo stack on new action
      }
    }

    case UNDO: {
      if (state.undoStack.length === 0) {
        return state
      }

      const newUndoStack = [...state.undoStack]
      newUndoStack.pop() // Remove the last item (this becomes the current state)
      const newRedoStack = enforceMaxSize([...state.redoStack, action.payload.currentState])

      return {
        undoStack: newUndoStack,
        redoStack: newRedoStack,
      }
    }

    case REDO: {
      if (state.redoStack.length === 0) {
        return state
      }

      const newRedoStack = [...state.redoStack]
      newRedoStack.pop() // Remove the last item (this becomes the current state)
      const newUndoStack = enforceMaxSize([...state.undoStack, action.payload.currentState])

      return {
        undoStack: newUndoStack,
        redoStack: newRedoStack,
      }
    }

    case CLEAR_HISTORY: {
      return initialUndoRedoState
    }

    case CLEAR_REDO_HISTORY: {
      return {
        ...state,
        redoStack: [],
      }
    }

    case RESTORE_HISTORY: {
      // Restore both undo and redo stacks from sessionStorage (on page load)
      // Enforce max size in case sessionStorage contains more items than allowed
      return {
        undoStack: enforceMaxSize(action.payload.undoStack),
        redoStack: enforceMaxSize(action.payload.redoStack),
      }
    }

    default:
      return state
  }
}
